import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { getProductStatistics } from '../api/products';

const LowStockAlert = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [lowStockCount, setLowStockCount] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const fetchLowStockCount = async () => {
            try {
                const stats = await getProductStatistics();
                const count = parseInt(stats.low_stock_products) || 0;
                setLowStockCount(count);
            } catch (error) {
                // On error, set count to 0 to hide the alert
                setLowStockCount(0);
            } finally {
                setIsLoaded(true);
            }
        };

        fetchLowStockCount();

        // Refresh count every 5 minutes
        const interval = setInterval(fetchLowStockCount, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    // Auto-reappear after 60 seconds if dismissed
    useEffect(() => {
        if (!isVisible) {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 60 * 1000); // 60 seconds

            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    const handleDismiss = () => {
        setIsVisible(false);
    };

    const handleViewLowStock = () => {
        // Navigate to products page with low stock filter
        navigate('/products?lowStockOnly=true');
    };

    // Don't show if not loaded, not visible, or no low stock products
    if (!isLoaded || !isVisible || lowStockCount === 0) {
        return null;
    }

    // Don't show on login page
    if (location.pathname === '/login') {
        return null;
    }

    return (
        <div className="bg-red-50 border-b-2 border-red-300 px-3 sm:px-4 py-2 sm:py-3 relative">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-red-900 truncate sm:whitespace-normal">
                            <span className="font-bold">{lowStockCount}</span> {lowStockCount === 1 ? 'product' : 'products'} <span className="hidden xs:inline">have</span> low stock
                        </p>
                        <p className="text-xs text-red-700 hidden md:block">
                            Click "View Low Stock" to see products that need restocking
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 flex-shrink-0">
                    <button
                        onClick={handleViewLowStock}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 bg-red-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                    >
                        View
                        <span className="hidden sm:inline"> Low Stock</span>
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="p-1 text-red-600 hover:text-red-900 transition-colors"
                        title="Dismiss alert"
                    >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LowStockAlert;
